import React from 'react';

import Icon from 'core/modules/atoms/Icon';
import Loading from 'core/modules/atoms/Loading';
import TooltipCustom from 'core/modules/atoms/Tooltip/Tooltip';
import type { RadarAlert } from '../../../../types/radar';
import RadarTypeTag from '../../RadarTypeTag/RadarTypeTag';
import SeverityBadge from '../../SeverityIndicator/SeverityBadge';
import './_learning-block-content.scss';
import useLearningBlockContent, { formatPercentage, getPrerequisiteColorClass, getPrerequisiteStatusKey } from './useLearningBlockContent';

type LearningBlockContentProps = {
  alert: RadarAlert;
  isMockMode?: boolean;
};

function LearningBlockContent({ alert, isMockMode }: LearningBlockContentProps): JSX.Element {
  const { detail, error, handleToggleRollbackInfo, hasRollbacks, isLoading, isRollbackInfoExpanded, locationItems, t } = useLearningBlockContent({
    annotationId: alert.id,
    isMockMode,
  });

  if (isLoading) {
    return (
      <div className="alert-detail-drawer__loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert-detail-drawer__error">
        <p>{t('chatbot:ai_analytics_student_conversation_error')}</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="alert-detail-drawer__empty">
        <p>{t('chatbot:ai_analytics_student_conversation_empty')}</p>
      </div>
    );
  }

  return (
    <div className="learning-block-content">
      <div className="learning-block-content__header">
        <div className="learning-block-content__label-row">
          <div className="learning-block-content__label-group">
            <RadarTypeTag type="learning-block" />
            <TooltipCustom placement="bottom-start" title={t('chatbot:radar_detail_learning_block_tooltip')}>
              <span>
                <Icon color="grey-03" size="default" type="info-circle" />
              </span>
            </TooltipCustom>
          </div>
          <SeverityBadge severity={alert.severity} />
        </div>
        <div className="learning-block-content__kc-info">
          <h3 className="learning-block-content__kc-statement">{detail.kcStatement}</h3>
          {locationItems.length > 0 && (
            <ul className="learning-block-content__locations">
              {locationItems.map((loc) => (
                <li key={loc} className="learning-block-content__location">
                  {loc}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="learning-block-content__stats">
        <div className="learning-block-content__stat-card">
          <div className="learning-block-content__stat-icon learning-block-content__stat-icon--success">
            <Icon type="success" size="default" />
          </div>
          <div className="learning-block-content__stat-info">
            <span className="learning-block-content__stat-value">
              {detail.correctCount}/{detail.attemptCount}
            </span>
            <span className="learning-block-content__stat-label">{t('chatbot:radar_detail_correct_activities')}</span>
          </div>
        </div>

        {hasRollbacks && (
          <div className="learning-block-content__stat-card learning-block-content__stat-card--expandable">
            <div className="learning-block-content__stat-row">
              <div className="learning-block-content__stat-icon learning-block-content__stat-icon--rollback">
                <Icon type="refresh" size="default" />
              </div>
              <div className="learning-block-content__stat-info">
                <span className="learning-block-content__stat-value">{detail.rollbackCount}</span>
                <span className="learning-block-content__stat-label">{t('chatbot:radar_detail_rollbacks')}</span>
              </div>
              <button
                aria-expanded={isRollbackInfoExpanded}
                aria-label={t('chatbot:radar_detail_rollbacks')}
                className={`learning-block-content__stat-toggle ${isRollbackInfoExpanded ? 'learning-block-content__stat-toggle--expanded' : ''}`}
                onClick={handleToggleRollbackInfo}
                type="button"
              >
                <Icon type="arrow-down" size="default" color="grey-03" />
              </button>
            </div>

            <div className="learning-block-content__rollback-info">
              <p className="learning-block-content__rollback-info-text" dangerouslySetInnerHTML={{ __html: t('chatbot:radar_detail_rollback_info') }} />
            </div>

            {isRollbackInfoExpanded && detail.prerequisites.length > 0 && (
              <div className="learning-block-content__prerequisites">
                <ul className="learning-block-content__prerequisite-list">
                  {detail.prerequisites.map((prereq) => {
                    const levelClass = getPrerequisiteColorClass(prereq.status);
                    const statusKey = getPrerequisiteStatusKey(prereq.status);

                    return (
                      <li className={`learning-block-content__prerequisite learning-block-content__prerequisite--${levelClass}`} key={prereq.kcId}>
                        <span className="learning-block-content__prerequisite-name">{prereq.statement}</span>
                        <div className="learning-block-content__prerequisite-bar-wrapper">
                          <span className="learning-block-content__prerequisite-marker" style={{ left: formatPercentage(prereq.currentKnowledge) }}>
                            <Icon type="chevron-down-fill" size="default" />
                          </span>
                          <div className="learning-block-content__prerequisite-bar">
                            <div className="learning-block-content__prerequisite-bar-fill" style={{ width: formatPercentage(prereq.currentKnowledge) }} />
                          </div>
                        </div>
                        <div className="learning-block-content__prerequisite-footer">
                          <span className={`learning-block-content__prerequisite-status learning-block-content__prerequisite-status--${levelClass}`}>
                            {t(`chatbot:${statusKey}`)}
                          </span>
                          <span className="learning-block-content__prerequisite-mastery">
                            {t('chatbot:radar_detail_content_mastery')} <strong>{formatPercentage(prereq.currentKnowledge)}</strong>
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LearningBlockContent;
